import React from 'react';
import clsx from 'clsx';

import styles from './Chat.module.css';
import { ChatWindow } from './ChatWindow';
import { AnimatedButton } from 'components/Map/AnimatedButton';
import { ReduxState } from 'ducks';
import { useSelector } from 'react-redux';
import { WelcomeBubble } from './WelcomeBubble';

interface ChatWidgetProps {
  digitalMapId?: string;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ digitalMapId }) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [bubbleDismissed, setBubbleDismissed] = React.useState<boolean>(false);
  const chatInputRef = React.useRef<HTMLTextAreaElement>(null);

  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const conversations = useSelector((state: ReduxState) => state.chatConversations.conversations);
  const loading = useSelector((state: ReduxState) => state.chatConversations.awaitingReply);

  // Check if there are any conversations for the current digital map
  const hasConversations = conversations.length > 0;

  // Check if bubble should be shown (both settings required)
  const bubbleEnabled =
    settings.chat_settings?.enable_chat_bubble &&
    !loading &&
    !!settings.chat_settings?.chat_bubble_text;

  // Use chat_bubble_text for bubble display
  const bubbleText = settings.chat_settings?.chat_bubble_text;

  // Show bubble only if enabled and has text
  const showWelcomeBubble = bubbleEnabled && !hasConversations && !open && !bubbleDismissed;

  const toggleOpen = (): void => {
    if (open) {
      setOpen(false);
    } else {
      setOpen(true);
      setBubbleDismissed(true); // Dismiss bubble when chat opens
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.focus();
        }
      }, 100);
    }
  };

  return (
    <>
      {showWelcomeBubble && (
        <WelcomeBubble
          message={bubbleText!}
          onClick={() => {
            setOpen(true);
            setBubbleDismissed(true);
          }}
        />
      )}
      {settings.chat_settings?.chat_icon_url ? (
        <AnimatedButton
          className={clsx(styles.chatButton, styles.customIcon)}
          clickedClassName={styles.clicked}
          onClick={toggleOpen}
        >
          <img
            src={settings.chat_settings.chat_icon_url}
            className={clsx(styles.chatIcon, styles.customIcon)}
            alt="chat"
          />
        </AnimatedButton>
      ) : (
        <AnimatedButton
          className={styles.chatButton}
          clickedClassName={styles.clicked}
          onClick={toggleOpen}
        >
          <img src="/static/images/map/nutmeg-ai-logo.svg" className={styles.chatIcon} alt="chat" />
        </AnimatedButton>
      )}
      <div className={clsx(styles.overlay, open && styles.open)}>
        <ChatWindow
          digitalMapId={digitalMapId}
          onClose={() => {
            setOpen(false);
            window.parent.postMessage('closeChat', '*');
          }}
          className={clsx(open && styles.open, styles.chatWidgetWindow)}
        />
      </div>
    </>
  );
};
